import React, { useState, useEffect, useRef } from 'react';
import { ConfigProvider, Table ,TableColumnsType, TableProps} from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { dateFormater } from '../../helper';

import './styles.css'
import TextAreaComponent from '../TextArea';
import InputField from '../InputField';
import { Control } from 'react-hook-form';
import Checkbox from '../Checkbox';

interface FieldConfig {
  type: 'input' | 'text-area'|'checkbox';
  placeholder?: string;
}

export type DynamicTableProps<T extends Record<string, any>> = {
  data: T[];
  showPagination?: boolean;
  renderEmpty?: () => JSX.Element;
  control?: Control<T>;
  fieldConfig?: Record<keyof T, FieldConfig>;
  customSetValueFn?:any;
  resizable?: boolean;
  sortable?: boolean;
};

interface ResizableTableProps {
  width?: number;
  onResize?: (e: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
  [key: string]: any;
}

const ResizableTitle: React.FC<ResizableTableProps> = ({ onResize, width, children, ...restProps }) => {
  const [isResizing, setIsResizing] = useState(false);
  const ref = useRef<HTMLTableHeaderCellElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !ref.current) return;

      const newWidth = e.clientX - ref.current.getBoundingClientRect().left;
      if (newWidth > 50) { // Minimum width
        if (onResize) {
          onResize(newWidth as any);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onResize]);

  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <th
      {...restProps}
      ref={ref}
      style={{ ...restProps.style, width, position: 'relative', userSelect: isResizing ? 'none' : 'auto' }}
    >
      {children}
      <span
        className="column-resize-handle"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
      />
    </th>
  );
};


const customizeRenderEmpty = () => (
  <div className="flex items-center justify-center p-20">
    <p className="text-gray-500 text-16">No Data Available</p>
  </div>
);

// Generate columns from record.

export const createColumns = <T extends Record<string, any>>(
  record: T,
  fieldConfig?: Record<keyof T | string, FieldConfig>,
  control?: Control<T>,
  dateKeys: string[] = [],
  sortable: boolean = false
): ColumnsType<T> => {
  const normalizedFieldConfig = fieldConfig
    ? Object.fromEntries(
        Object.entries(fieldConfig).map(([k, v]) => [k.toLowerCase(), v])
      )
    : undefined;

  // Helper to create sorter based on data type
  const getSorter = (key: string, isDateField: boolean, sampleValue: any) => {
    if (!sortable) return undefined;

    if (isDateField) {
      return (a: any, b: any) => {
        const dateA = a[key] ? new Date(a[key]).getTime() : 0;
        const dateB = b[key] ? new Date(b[key]).getTime() : 0;
        return dateA - dateB;
      };
    }

    if (typeof sampleValue === 'number') {
      return (a: any, b: any) => {
        const numA = a[key] ?? 0;
        const numB = b[key] ?? 0;
        return numA - numB;
      };
    }

    // Default string sort
    return (a: any, b: any) => {
      const strA = String(a[key] ?? '').toLowerCase();
      const strB = String(b[key] ?? '').toLowerCase();
      return strA.localeCompare(strB);
    };
  };

  return Object.keys(record || {})
    .filter((key) => key.toLowerCase() !== 'key')
    .map((key) => {
      const lowerKey = key.toLowerCase();
      const isDateField = dateKeys.includes(key);
      const sampleValue = record[key as keyof T];

      // Date formatting
      if (isDateField) {
        return {
          title: key.replace(/_/g, ' ').split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' '),
          dataIndex: key,
          key,
          width: 150,
          sorter: getSorter(key, true, sampleValue),
          render: (value: any) => dateFormater(value),
          ellipsis: true,
        };
      }

      const config = normalizedFieldConfig?.[lowerKey];
      if (config) {
        const isTextArea = config.type === 'text-area';
        const Component = isTextArea ? TextAreaComponent :  InputField;
        const placeholder = config.placeholder
          ? config.placeholder
          : isTextArea
          ? 'Enter a description...'
          : 'Enter event name...';

        return {
          title: key.replace(/_/g, ' ').split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' '),
          dataIndex: key,
          key,
          width: 300,
          sorter: getSorter(key, false, sampleValue),
          render: (_: any, row: T, rowIndex: number) =>
            control ? (
              <Component
                control={control}
                name={`data[${rowIndex}].${key}`}
                defaultValue={row[key as keyof T]}
                placeholder={placeholder}
                className={isTextArea ? 'rounded-3xl shadow-sm' : undefined}
              />
            ) : (
              <>{row[key as keyof T]}</>
            ),
          ellipsis: true,
        };
      }

      const value = record[key as keyof T];
      if (Array.isArray(value) || (value && typeof value === 'object')) {
        // Hide complex nested columns - they'll be shown in expandable row
        return null;
      }

      return {
        title: key.replace(/_/g, ' ').split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' '),
        dataIndex: key,
        key,
        width: 150,
        sorter: getSorter(key, false, sampleValue),
        render: (text: any) => {
          if (text === undefined || text === null) return '-';
          if (typeof text === 'boolean') return String(text);
          return text;
        },
        ellipsis: true,
      };
    })
    .filter(Boolean) as ColumnsType<T>;
};

const dateKeys = ['updated_at', 'created_at', 'published_at'];

// Recursively render nested data for expandable rows.
export const renderNestedData = (record: any, showPagination?: boolean): React.ReactNode => {
  const nestedKeys = Object.keys(record).filter(
    (key) =>
      Array.isArray(record[key]) || (record[key] && typeof record[key] === 'object')
  );

  return (
    <div className="ml-4 py-3">
      {nestedKeys.map((key) => (
        <div key={key} className="mb-4 last:mb-0 px-4">
          {key !== 'value' && (
            <div className="mb-2 pb-2 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {key.replace(/_/g, ' ')}
              </h4>
            </div>
          )}
          <div className="bg-white rounded-md shadow-sm overflow-hidden">
            <DynamicTable data={record[key]} showPagination={showPagination} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const normalizeData = (data: any): any[] => {
  let tableData: any[] = [];
  if (Array.isArray(data)) {
    tableData = data.map((item, index) => ({ ...item, key: index }));
  } else if (data && typeof data === 'object') {
    tableData = Object.keys(data).map((key) => ({
      key,
      name: key,
      value: data[key],
    }));
  }
  return tableData;
};

// DynamicTable component renders a table with nested expandable rows.
export const DynamicTable: React.FC<DynamicTableProps<any>> = ({
  data,
  showPagination,
  renderEmpty,
  control,
  fieldConfig,
  customSetValueFn,
  resizable = true,
  sortable = false
}) => {
  const tableData = normalizeData(data);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  let initialColumns: ColumnsType<any> = [];
  if (tableData.length > 0) {
    // console.log('DynamicTable - tableData:', tableData);

    // Merge keys from all records to handle cases where different rows have different structures
    const allKeys = new Set<string>();
    tableData.forEach(record => {
      Object.keys(record).forEach(key => {
        if (key.toLowerCase() !== 'key') {
          const value = record[key];
          // Only include non-nested fields in the main table
          if (!Array.isArray(value) && !(value && typeof value === 'object')) {
            allKeys.add(key);
          }
        }
      });
    });

    // console.log('DynamicTable - allKeys:', Array.from(allKeys));

    // Create a representative record with all keys for column generation
    const representativeRecord: any = {};
    allKeys.forEach(key => {
      // Find first record that has this key
      const recordWithKey = tableData.find(r => key in r);
      if (recordWithKey) {
        representativeRecord[key] = recordWithKey[key];
      }
    });

    // console.log('DynamicTable - representativeRecord:', representativeRecord);

    initialColumns = representativeRecord && Object.keys(representativeRecord).length > 0
      ? createColumns(representativeRecord, fieldConfig, control, dateKeys, sortable)
      : createColumns(tableData[0], fieldConfig, control, dateKeys, sortable);

    // console.log('DynamicTable - initialColumns:', initialColumns);
  }

  // Initialize column widths
  useEffect(() => {
    const widths: Record<string, number> = {};
    initialColumns.forEach((col: any) => {
      if (col.key) {
        widths[col.key] = col.width || 150;
      }
    });
    setColumnWidths(widths);
  }, [data]);

  const handleResize = (key: string) => (width: number) => {
    setColumnWidths((prev) => ({ ...prev, [key]: width }));
  };

  const mergedColumns = resizable
    ? initialColumns.map((col: any) => ({
        ...col,
        width: columnWidths[col.key] || col.width || 150,
        onHeaderCell: () => ({
          width: columnWidths[col.key] || col.width || 150,
          onResize: handleResize(col.key),
        }),
      }))
    : initialColumns;

  const renderEmptyComponent = renderEmpty || customizeRenderEmpty;
// rowSelection object indicates the need for row selection
const rowSelection: TableProps<any>['rowSelection'] = {
  onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
    customSetValueFn(selectedRows)
  },
  getCheckboxProps: (record: any) => ({
    disabled: record.name === 'Disabled User', // Column configuration not to be checked
    name: record.name,
  }),
};
  return (
    <ConfigProvider renderEmpty={renderEmptyComponent}>
      <Table
        className="clean-table"
        components={resizable ? {
          header: {
            cell: ResizableTitle,
          },
        } : undefined}
        columns={mergedColumns}
        dataSource={tableData}
        pagination={showPagination ? { position: ['bottomCenter'] } : false}
        rowSelection={customSetValueFn ? { type: 'checkbox', ...rowSelection } : undefined}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record) => renderNestedData(record, showPagination),
          rowExpandable: (record) =>
            Object.values(record).some(
              (value) => Array.isArray(value) || (value && typeof value === 'object')
            ),
        }}
      />
    </ConfigProvider>
  );
};

export default DynamicTable;